import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { computePlan } from "@/lib/planEngine";
import { format } from "date-fns";
import DailyNutritionCard from "./DailyNutritionCard";
import YourNextMealCard from "./YourNextMealCard";
import DailyMealPreviewDialog from "./DailyMealPreviewDialog";

const DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export default function DailyNutritionSection() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [previewOpen, setPreviewOpen] = useState(false);
  const [highlightMealName, setHighlightMealName] = useState(null);

  const todayName = DAYS[new Date().getDay()];
  const todayDate = format(new Date(), "yyyy-MM-dd");
  const todayDay = todayName.toLowerCase();

  // User's daily calorie & macro goals from computed plan
  const userPlan = user ? computePlan(user) : null;

  // 1. Fetch user's weekly meal plan
  const { data: weeklyMealPlan = null } = useQuery({
    queryKey: ["userWeeklyMealPlan", user?.id],
    queryFn: () =>
      base44.entities.UserWeeklyMealPlan.filter({ user_id: user.id }).then(
        (results) => results[0] || null
      ),
    enabled: !!user,
  });

  // 2. Fetch templates
  const { data: templates = [] } = useQuery({
    queryKey: ["mealPlanTemplates"],
    queryFn: () => base44.entities.MealPlanTemplate.list(),
  });

  // 3. Fetch today's meal completions
  const { data: mealCompletions = [] } = useQuery({
    queryKey: ["meal-completions", todayDate],
    queryFn: () =>
      base44.entities.MealCompletion.filter({
        user_id: user.id,
        completed_date: todayDate,
      }),
    enabled: !!user,
  });

  // 4. Fetch manual meal logs
  const { data: manualLogs = [] } = useQuery({
    queryKey: ["meal-logs", todayDate, user?.id],
    queryFn: () =>
      base44.entities.MealLog.filter(
        { user_id: user.id, completed_date: todayDate },
        "created_date"
      ),
    enabled: !!user,
  });

  // Active plan for today
  const templateId = weeklyMealPlan?.[todayDay];
  const activePlan = templateId
    ? templates.find((t) => t.id === templateId)
    : null;

  const todaysMeals = useMemo(() => {
    return activePlan
      ? (activePlan.meals || []).map((meal) => ({
          ...meal,
          plan_id: activePlan.id,
          plan_name: activePlan.name,
        }))
      : [];
  }, [activePlan]);

  const completedMeals = useMemo(() => {
    return new Set(
      mealCompletions.map((mc) => `${mc.meal_plan_id}-${mc.meal_name}`)
    );
  }, [mealCompletions]);

  // Mutations
  const completeMealMutation = useMutation({
    mutationFn: (data) => base44.entities.MealCompletion.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meal-completions"] });
    },
  });

  const uncompleteMealMutation = useMutation({
    mutationFn: (id) => base44.entities.MealCompletion.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meal-completions"] });
    },
  });

  const updateLogMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.MealLog.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meal-logs"] });
    },
  });

  const handleToggleMeal = (planId, mealName) => {
    const key = `${planId}-${mealName}`;
    if (completedMeals.has(key)) {
      const completion = mealCompletions.find(
        (mc) => mc.meal_plan_id === planId && mc.meal_name === mealName
      );
      if (completion) uncompleteMealMutation.mutate(completion.id);
    } else {
      completeMealMutation.mutate({
        meal_plan_id: planId,
        meal_name: mealName,
        completed_date: todayDate,
        user_id: user.id,
      });
    }
  };

  const handleToggleManual = (logId, currentStatus) => {
    updateLogMutation.mutate({
      id: logId,
      data: { completed: currentStatus },
    });
  };

  // Nutrition targets
  const targetCalories =
    userPlan?.target_calories || activePlan?.daily_calories || 2000;
  const targetProtein =
    userPlan?.target_protein ||
    activePlan?.daily_protein_g ||
    Math.round((targetCalories * 0.3) / 4);
  const targetCarbs =
    userPlan?.target_carbs ||
    activePlan?.daily_carbs_g ||
    Math.round((targetCalories * 0.45) / 4);
  const targetFats =
    userPlan?.target_fats ||
    activePlan?.daily_fats_g ||
    Math.round((targetCalories * 0.25) / 9);

  // Consumed calculations
  const consumedCalories = useMemo(() => {
    const planCals = todaysMeals
      .filter((m) => completedMeals.has(`${m.plan_id}-${m.meal_name}`))
      .reduce((sum, m) => {
        return (
          sum +
          (m.foods || []).reduce((s, f) => s + (Number(f.calories) || 0), 0)
        );
      }, 0);
    const manualCals = manualLogs
      .filter((l) => l.completed)
      .reduce((s, l) => s + (Number(l.calories) || 0), 0);
    return Math.round(planCals + manualCals);
  }, [todaysMeals, completedMeals, manualLogs]);

  const consumedProtein = useMemo(() => {
    const planP = todaysMeals
      .filter((m) => completedMeals.has(`${m.plan_id}-${m.meal_name}`))
      .reduce((sum, m) => {
        return (
          sum +
          (m.foods || []).reduce((s, f) => s + (Number(f.protein_g) || 0), 0)
        );
      }, 0);
    const manualP = manualLogs
      .filter((l) => l.completed)
      .reduce((s, l) => s + (Number(l.protein_g) || 0), 0);
    return Math.round(planP + manualP);
  }, [todaysMeals, completedMeals, manualLogs]);

  const consumedCarbs = useMemo(() => {
    const planC = todaysMeals
      .filter((m) => completedMeals.has(`${m.plan_id}-${m.meal_name}`))
      .reduce((sum, m) => {
        return (
          sum +
          (m.foods || []).reduce((s, f) => s + (Number(f.carbs_g) || 0), 0)
        );
      }, 0);
    const manualC = manualLogs
      .filter((l) => l.completed)
      .reduce((s, l) => s + (Number(l.carbs_g) || 0), 0);
    return Math.round(planC + manualC);
  }, [todaysMeals, completedMeals, manualLogs]);

  const consumedFats = useMemo(() => {
    const planF = todaysMeals
      .filter((m) => completedMeals.has(`${m.plan_id}-${m.meal_name}`))
      .reduce((sum, m) => {
        return (
          sum +
          (m.foods || []).reduce((s, f) => s + (Number(f.fats_g) || 0), 0)
        );
      }, 0);
    const manualF = manualLogs
      .filter((l) => l.completed)
      .reduce((s, l) => s + (Number(l.fats_g) || 0), 0);
    return Math.round(planF + manualF);
  }, [todaysMeals, completedMeals, manualLogs]);

  // Next meal resolution: find the first uncompleted planned meal
  const nextMeal = useMemo(() => {
    if (!todaysMeals.length) return null;
    return todaysMeals.find(
      (m) => !completedMeals.has(`${m.plan_id}-${m.meal_name}`)
    ) || null;
  }, [todaysMeals, completedMeals]);

  const totalMealsCount = todaysMeals.length;
  const completedCount = todaysMeals.filter((m) =>
    completedMeals.has(`${m.plan_id}-${m.meal_name}`)
  ).length;
  const allMealsLogged =
    totalMealsCount > 0 && completedCount === totalMealsCount;

  const handleOpenMeal = (mealName) => {
    setHighlightMealName(mealName);
    setPreviewOpen(true);
  };

  return (
    <div className="space-y-4">
      {/* 5. DAILY NUTRITION — CLEAN SUMMARY CARD */}
      <DailyNutritionCard
        consumedCalories={consumedCalories}
        targetCalories={targetCalories}
        consumedProtein={consumedProtein}
        targetProtein={targetProtein}
        consumedCarbs={consumedCarbs}
        targetCarbs={targetCarbs}
        consumedFats={consumedFats}
        targetFats={targetFats}
        onOpenPreview={() => setPreviewOpen(true)}
      />

      {/* 6. YOUR NEXT MEAL CARD */}
      <YourNextMealCard
        nextMeal={nextMeal}
        allMealsLogged={allMealsLogged}
        totalMealsCount={totalMealsCount}
        onOpenMeal={handleOpenMeal}
      />

      {/* DAILY MEAL PREVIEW MODAL */}
      <DailyMealPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        activePlan={activePlan}
        todaysMeals={todaysMeals}
        manualLogs={manualLogs}
        completedMeals={completedMeals}
        onToggleMeal={handleToggleMeal}
        onToggleManual={handleToggleManual}
        isMutating={
          completeMealMutation.isPending || uncompleteMealMutation.isPending
        }
        highlightMealName={highlightMealName}
      />
    </div>
  );
}
